import axios from "axios";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
	Button,
	Card,
	DatePicker,
	Empty,
	Flex,
	Popconfirm,
	Select,
	Table,
	Typography,
	notification,
} from "antd";
import {
	DeleteFilled,
	DragOutlined,
	EditFilled,
	PlusCircleFilled,
	SendOutlined,
	TeamOutlined,
	UnorderedListOutlined,
	UserAddOutlined,
	UsergroupAddOutlined,
} from "@ant-design/icons";
import Layout from "../layout/Layout";
import { api } from "../utils/Api";
import { CustomSpinner } from "../utils/CustomComponents";
import DriverShiftNewModal from "./DriverShiftNewModal";
import DriverShiftGroupNewModal from "./DriverShiftGroupNewModal";
import DriverShiftGroupMemberModal from "./DriverShiftGroupMemberModal";
import DriverShiftGroupNameModal from "./DriverShiftGroupNameModal";
import DriverShiftGroupMoveModal from "./DriverShiftGroupMoveModal";
import DriverShiftEditModal from "./DriverShiftEditModal";
import DriverShiftHistoryModal from "./DriverShiftHistoryModal";
import DriverDefaultGroupsModal from "./DriverDefaultGroupsModal";
const { Text } = Typography;

const DriverDashboard = () => {
	const [loadingPlants, setLoadingPlants] = useState(true);
	const [plantOptions, setPlantOptions] = useState([]);
	const [date, setDate] = useState(dayjs());
	const [plant, setPlant] = useState(null);
	const [modalNewShiftOpen, setModalNewShiftOpen] = useState(false);
	const [shifts, setShifts] = useState([]);
	const [loadingShifts, setLoadingShifts] = useState(false);
	const [selectedShift, setSelectedShift] = useState(null);
	const [shiftGroups, setShiftGroups] = useState({});
	const [loadingShiftGroups, setLoadingShiftGroups] = useState(false);
	// { shift, group, memberIndex } — memberIndex null when adding a member
	const [memberModal, setMemberModal] = useState(null);
	const [nameModalGroup, setNameModalGroup] = useState(null);
	// { shift, group, memberIndex } of the member being moved to another group
	const [moveModal, setMoveModal] = useState(null);
	const [sendingShifts, setSendingShifts] = useState(false);
	const [editShift, setEditShift] = useState(null);
	const [historyShift, setHistoryShift] = useState(null);
	const [defaultGroupsOpen, setDefaultGroupsOpen] = useState(false);
	const [apiNotification, contextHolder] = notification.useNotification();

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	useEffect(() => {
		getPlants();
	}, []);

	useEffect(() => {
		if (plant) {
			getShifts();
		} else {
			setShifts([]);
			setShiftGroups({});
		}
	}, [date, plant]);

	const getPlants = async () => {
		const url = api + "/plant/list/57Concrete";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoadingPlants(true);
			const { data } = await axios.get(url);
			const plantList = data.payload.map((item) => ({
				label: item.name,
				value: item._id,
			}));
			setPlantOptions(plantList);
		} catch (err) {
			console.error(err.message);
		} finally {
			setLoadingPlants(false);
		}
	};

	const getShifts = async () => {
		const url = api + "/shift/list";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoadingShifts(true);
			const { data } = await axios.get(url, {
				params: {
					date: date.toDate().toISOString(),
					plant: plant,
					timezoneOffset: date.toDate().getTimezoneOffset(),
				},
			});
			if (data.success) {
				const shiftList = data.payload.map((item) => ({
						key: item._id,
						id: item._id,
						rawDate: item.date,
						date: dayjs(item.date).format("MM/DD/YYYY hh:mm A"),
						plant: item.plant?.name ?? "",
						createdBy: item.createdBy?.name ?? "",
						createdAt: dayjs(item.createdAt).format("MM/DD/YYYY hh:mm A"),
						rawCreatedAt: item.createdAt,
						history: item.history || [],
				}));
				setShifts(shiftList);
				getShiftGroups(shiftList.map((item) => item.id));
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to get shifts");
		} finally {
			setLoadingShifts(false);
		}
	};

	const getShiftGroups = async (shiftIds) => {
		const url = api + "/shift-group/list";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoadingShiftGroups(true);
			const responses = await Promise.all(
				shiftIds.map((shiftId) => axios.get(url, { params: { shift: shiftId } })),
			);
			const groupsByShift = {};
			responses.forEach(({ data }, index) => {
				if (data.success) {
					groupsByShift[shiftIds[index]] = data.payload;
				} else {
					openNotification("error", "Error", data.message);
				}
			});
			setShiftGroups(groupsByShift);
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to get groups");
		} finally {
			setLoadingShiftGroups(false);
		}
	};

	// Success notification, or a warning listing the drivers whose SMS couldn't be sent
	const notifySmsResult = (data) => {
		const failed = data.payload?.sms?.failed || [];
		if (failed.length === 0) {
			openNotification("success", "Success", data.message);
		} else {
			openNotification(
				"warning",
				data.message,
				`Skipped: ${failed
					.map((item) => `${item.driver} (${item.reason})`)
					.join(", ")}`,
			);
		}
	};

	const deleteMember = async (group, memberIndex) => {
		const url = api + `/shift-group/member/${group._id}/${memberIndex}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.delete(url, {
				params: { timezoneOffset: new Date().getTimezoneOffset() },
			});
			if (data.success) {
				getShiftGroups(shifts.map((item) => item.id));
				notifySmsResult(data);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to delete member");
		}
	};

	const deleteGroup = async (group) => {
		const url = api + `/shift-group/delete/${group._id}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.delete(url, {
				params: { timezoneOffset: new Date().getTimezoneOffset() },
			});
			if (data.success) {
				getShiftGroups(shifts.map((item) => item.id));
				notifySmsResult(data);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to delete group");
		}
	};

	const sendShifts = async () => {
		const url = api + "/shift-group/send";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setSendingShifts(true);
			const { data } = await axios.post(url, {
				shifts: shifts.map((item) => item.id),
				timezoneOffset: new Date().getTimezoneOffset(),
			});
			if (data.success) {
				const { failed } = data.payload;
				if (failed.length === 0) {
					openNotification("success", "Success", data.message);
				} else {
					openNotification(
						"warning",
						data.message,
						`Skipped: ${failed
							.map((item) => `${item.driver} (${item.reason})`)
							.join(", ")}`,
					);
				}
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to send shifts");
		} finally {
			setSendingShifts(false);
		}
	};

	const hasMembersToSend = shifts.some((shift) =>
		(shiftGroups[shift.id] || []).some((group) => group.members.length > 0),
	);

	// Drivers or trucks already assigned in the groups of a shift, except the member being edited
	const getUsedValues = (field, shiftId, editedGroupId, editedMemberIndex) =>
		(shiftGroups[shiftId] || []).flatMap((group) =>
			group.members
				.filter(
					(member, index) =>
						!(group._id === editedGroupId && index === editedMemberIndex),
				)
				.map((member) => member[field]?._id)
				.filter(Boolean),
		);

	const formatDate = (value) => dayjs(value).format("MM/DD/YYYY hh:mm A");

	const filterOption = (input, option) =>
		(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

	const RenderFilters = () => (
		<Flex justify="space-between" align="center" wrap="wrap" gap="middle">
			<Flex gap="middle" wrap="wrap">
				<DatePicker
					size="large"
					format="MM/DD/YYYY"
					value={date}
					onChange={(value) => setDate(value)}
					allowClear={false}
					disabled={loadingPlants}
					style={{ width: 200 }}
				/>
				<Select
					showSearch
					placeholder="Select plant"
					size="large"
					options={plantOptions}
					filterOption={filterOption}
					value={plant}
					onChange={(value) => setPlant(value)}
					loading={loadingPlants}
					disabled={loadingPlants}
					style={{ width: 250 }}
				/>
			</Flex>
			<Flex gap="middle" wrap="wrap">
				<Button
					type="primary"
					size="large"
					icon={<PlusCircleFilled />}
					onClick={() => setModalNewShiftOpen(true)}
				>
					New Shift
				</Button>
				<Popconfirm
					title="Send shifts"
					description="An SMS with their shift will be sent to every driver in the groups. Continue?"
					okText="Send"
					cancelText="Cancel"
					onConfirm={sendShifts}
					disabled={!hasMembersToSend || sendingShifts}
				>
					<Button
						type="primary"
						size="large"
						icon={<SendOutlined />}
						loading={sendingShifts}
						disabled={!hasMembersToSend}
					>
						Send Shifts
					</Button>
				</Popconfirm>
				<Button
					size="large"
					icon={<TeamOutlined />}
					onClick={() => setDefaultGroupsOpen(true)}
				>
					View Default Groups
				</Button>
			</Flex>
		</Flex>
	);

	const RenderShiftGroups = ({ shift }) => {
		if (loadingShiftGroups) {
			return <CustomSpinner />;
		}

		const groups = shiftGroups[shift.id] || [];

		if (groups.length === 0) {
			return <Empty description="No groups created" />;
		}

		// Members sorted by shift start, keeping their position in the group for edit/delete.
		// Groups sorted by their earliest shift start; numbering continues across groups.
		let rowNumber = 0;
		const sortedGroups = groups
			.map((group) => ({
				group,
				rows: group.members
					.map((member, memberIndex) => ({ ...member, memberIndex }))
					.sort((a, b) => new Date(a.startDate) - new Date(b.startDate)),
			}))
			.sort(
				(a, b) =>
					new Date(a.rows[0]?.startDate ?? 0) -
					new Date(b.rows[0]?.startDate ?? 0),
			)
			.map(({ group, rows }) => ({
				group,
				rows: rows.map((row) => ({
					...row,
					key: row.memberIndex,
					number: ++rowNumber,
				})),
			}));

		return (
			<Flex vertical gap="middle">
				{sortedGroups.map(({ group, rows }) => {
					const columns = [
						{
							title: "#",
							dataIndex: "number",
							key: "number",
							width: 50,
						},
						{
							title: "Shift Start",
							key: "startDate",
							render: (_, member) => formatDate(member.startDate),
						},
						{
							title: "Shift End",
							key: "endDate",
							render: (_, member) => formatDate(member.endDate),
						},
						{
							title: "Driver",
							key: "driver",
							render: (_, member) => member.driver?.name ?? "",
						},
						{
							title: "Truck",
							key: "truck",
							render: (_, member) => member.truck?.number ?? "",
						},
						{
							title: "Phone",
							key: "phone",
							render: (_, member) => member.driver?.phone || "-",
						},
						{
							title: "Actions",
							key: "actions",
							width: 160,
							render: (_, member) => (
								<Flex gap="small">
									<Button
										icon={<DragOutlined />}
										disabled={group.members.length === 1 || groups.length === 1}
										title={
											group.members.length === 1
												? "A group must have at least one member"
												: groups.length === 1
												? "There is no other group to move to"
												: "Move to another group"
										}
										onClick={() =>
											setMoveModal({
												shift,
												group,
												memberIndex: member.memberIndex,
											})
										}
									/>
									<Button
										type="primary"
										icon={<EditFilled />}
										onClick={() =>
											setMemberModal({
												shift,
												group,
												memberIndex: member.memberIndex,
											})
										}
									/>
									<Popconfirm
										title="Delete member"
										description="The driver will be notified by SMS. Are you sure you want to delete this member?"
										okText="Delete"
										cancelText="Cancel"
										okButtonProps={{ danger: true }}
										onConfirm={() => deleteMember(group, member.memberIndex)}
										disabled={group.members.length === 1}
									>
										<Button
											danger
											type="primary"
											icon={<DeleteFilled />}
											disabled={group.members.length === 1}
											title={
												group.members.length === 1
													? "A group must have at least one member"
													: undefined
											}
										/>
									</Popconfirm>
								</Flex>
							),
						},
					];

					return (
						<Card
							key={group._id}
							title={
								<Flex align="center" gap="small">
									{group.name}
									<Button
										type="text"
										size="small"
										icon={<EditFilled />}
										onClick={() => setNameModalGroup(group)}
									/>
								</Flex>
							}
							size="small"
							extra={
								<Flex gap="small">
									<Button
										type="primary"
										icon={<UserAddOutlined />}
										onClick={() =>
											setMemberModal({ shift, group, memberIndex: null })
										}
									>
										Add Member
									</Button>
									<Popconfirm
										title="Delete group"
										description="Every driver of the group will be notified by SMS. Are you sure you want to delete this group and all its members?"
										okText="Delete"
										cancelText="Cancel"
										okButtonProps={{ danger: true }}
										onConfirm={() => deleteGroup(group)}
									>
										<Button danger type="primary" icon={<DeleteFilled />}>
											Delete Group
										</Button>
									</Popconfirm>
								</Flex>
							}
						>
							<Table
								columns={columns}
								dataSource={rows}
								pagination={false}
								size="small"
								scroll={{ x: true }}
							/>
						</Card>
					);
				})}
			</Flex>
		);
	};

	const RenderShifts = () => {
		if (loadingShifts) {
			return <CustomSpinner />;
		}

		if (shifts.length === 0) {
			return <Empty description="No shift found" />;
		}

		return shifts.map((shift) => (
			<Flex key={shift.id} vertical gap="small">
				<Flex align="center" gap="small">
					<Text>
						<Text strong>Assigned Date:</Text> {shift.date}
					</Text>
					<Button
						type="text"
						size="small"
						icon={<EditFilled />}
						title="Change shift date"
						onClick={() => setEditShift(shift)}
					/>
					<Button
						type="text"
						size="small"
						icon={<UnorderedListOutlined />}
						title="Shift date history"
						onClick={() => setHistoryShift(shift)}
					/>
				</Flex>
				<Text>
					<Text strong>Created At:</Text> {shift.createdAt}
				</Text>
				<Text>
					<Text strong>Created By:</Text> {shift.createdBy}
				</Text>
				<div>
					<Button
						type="primary"
						size="large"
						icon={<UsergroupAddOutlined />}
						onClick={() => setSelectedShift(shift)}
					>
						Create Group
					</Button>
				</div>
				<RenderShiftGroups shift={shift} />
			</Flex>
		));
	};

	return (
		<Layout title="Driver" description="Driver Dashboard">
			{contextHolder}
			<Flex vertical gap="large">
				<RenderFilters />
				<RenderShifts />
			</Flex>
			<DriverShiftGroupNewModal
				open={!!selectedShift}
				shift={selectedShift}
				plant={plant}
				usedDrivers={
					selectedShift ? getUsedValues("driver", selectedShift.id) : []
				}
				usedTrucks={
					selectedShift ? getUsedValues("truck", selectedShift.id) : []
				}
				onClose={() => setSelectedShift(null)}
				onSuccess={(message) => {
					setSelectedShift(null);
					getShiftGroups(shifts.map((item) => item.id));
					openNotification("success", "Success", message);
				}}
			/>
			<DriverShiftGroupMemberModal
				open={!!memberModal}
				shift={memberModal?.shift}
				group={memberModal?.group}
				memberIndex={memberModal?.memberIndex}
				plant={plant}
				usedDrivers={
					memberModal
						? getUsedValues(
								"driver",
								memberModal.shift.id,
								memberModal.group._id,
								memberModal.memberIndex,
						  )
						: []
				}
				usedTrucks={
					memberModal
						? getUsedValues(
								"truck",
								memberModal.shift.id,
								memberModal.group._id,
								memberModal.memberIndex,
						  )
						: []
				}
				onClose={() => setMemberModal(null)}
				onSuccess={(message, skipped) => {
					setMemberModal(null);
					getShiftGroups(shifts.map((item) => item.id));
					if (skipped) {
						openNotification("warning", message, skipped);
					} else {
						openNotification("success", "Success", message);
					}
				}}
			/>
			<DriverDefaultGroupsModal
				open={defaultGroupsOpen}
				onClose={() => setDefaultGroupsOpen(false)}
				plantOptions={plantOptions}
				loadingPlants={loadingPlants}
				dashboardPlant={plant}
			/>
			<DriverShiftHistoryModal
				open={!!historyShift}
				shift={historyShift}
				onClose={() => setHistoryShift(null)}
			/>
			<DriverShiftEditModal
				open={!!editShift}
				shift={editShift}
				onClose={() => setEditShift(null)}
				onSuccess={(message, skipped) => {
					setEditShift(null);
					getShifts();
					if (skipped) {
						openNotification("warning", message, skipped);
					} else {
						openNotification("success", "Success", message);
					}
				}}
			/>
			<DriverShiftGroupMoveModal
				open={!!moveModal}
				shift={moveModal?.shift}
				group={moveModal?.group}
				memberIndex={moveModal?.memberIndex}
				targetGroups={
					moveModal
						? (shiftGroups[moveModal.shift.id] || []).filter(
								(item) => item._id !== moveModal.group._id,
						  )
						: []
				}
				usedTrucks={
					moveModal
						? getUsedValues(
								"truck",
								moveModal.shift.id,
								moveModal.group._id,
								moveModal.memberIndex,
						  )
						: []
				}
				onClose={() => setMoveModal(null)}
				onSuccess={(message) => {
					setMoveModal(null);
					getShiftGroups(shifts.map((item) => item.id));
					openNotification("success", "Success", message);
				}}
			/>
			<DriverShiftGroupNameModal
				open={!!nameModalGroup}
				group={nameModalGroup}
				onClose={() => setNameModalGroup(null)}
				onSuccess={(message) => {
					setNameModalGroup(null);
					getShiftGroups(shifts.map((item) => item.id));
					openNotification("success", "Success", message);
				}}
			/>
			<DriverShiftNewModal
				open={modalNewShiftOpen}
				onClose={() => setModalNewShiftOpen(false)}
				plantOptions={plantOptions}
				loadingPlants={loadingPlants}
				defaultPlant={plant}
				onSuccess={(message) => {
					setModalNewShiftOpen(false);
					if (plant) {
						getShifts();
					}
					openNotification("success", "Success", message);
				}}
			/>
		</Layout>
	);
};

export default DriverDashboard;
